import { Injectable } from "@angular/core";
import { marked } from "marked";
import { MarkedJsExtensionLevel, MarkedJsExtensionThis, MarkedJsToken } from "@webapp/shared/rich-text-editor/marked-js/marked-js.interfaces";

@Injectable({
  providedIn: "root",
})
export class MarkedJsService {
  private hasLoadedExtensions = false;

  public loadCustomExtensionsForRichTextEditor(): void {
    if (!this.hasLoadedExtensions) {
      this.initMarkedExtensions();

      this.hasLoadedExtensions = true;
    }
  }

  private initMarkedExtensions(): void {
    const ghMentionName = "ghMention";
    const ghMentionRegexStart = /@\[([\p{L}0-9:@.\s\-_]*?)\]/u;
    const ghMentionRegex = /^@\[([\p{L}0-9:@.\s\-_]*?)\]/u;

    const ghMentionExtension = {
      name: ghMentionName,
      level: "inline" as MarkedJsExtensionLevel,
      start(src: string): number {
        return src.match(ghMentionRegexStart)?.index;
      },
      tokenizer(src: string): MarkedJsToken | void {
        const match = ghMentionRegex.exec(src);

        if (match) {
          return {
            type: ghMentionName,
            raw: match[0],
            text: match[0].trim(),
            tokens: [],
          };
        }
      },
      renderer({ raw }): string {
        const regex = /@\[(.*):(.*):(.*)\]/g;
        const [, name, email, id] = regex.exec(raw);

        return `<span class="mention" data-denotation-char="@" data-id=${id} data-value=${name} data-email=${email} ><span contenteditable="false"><span class="ql-mention-denotation-char">@</span>${name}</span></span>`;
      },
    };

    const ghEmojiStartRegex =
      /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/;
    const ghEmojiRegex =
      /^(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/;

    const ghEmojiName = "ghEmoji";

    const ghEmojiExtension = {
      name: ghEmojiName,
      level: "inline" as MarkedJsExtensionLevel,
      start(src: string): number {
        return src.match(ghEmojiStartRegex)?.index;
      },
      tokenizer(src: string): MarkedJsToken | void {
        const match = ghEmojiRegex.exec(src);

        if (match) {
          return {
            type: ghEmojiName,
            raw: match[0],
            text: match[0].trim(),
            tokens: [],
          };
        }
      },
      renderer({ raw }: MarkedJsToken): string {
        return `<span class="emoji">${raw}</span>`;
      },
    };

    const ghStrikeExtensionName = "strikethrough";
    const ghStrikeThroughStartRegex = /~~(.*?)~~/;
    const ghStrikeThroughRegex = /^~~(.*?)~~/;

    const ghStrikeThroughExtension = {
      name: ghStrikeExtensionName,
      level: "inline" as MarkedJsExtensionLevel,
      start(src: string): number {
        return src.match(ghStrikeThroughStartRegex)?.index;
      },
      tokenizer(this: MarkedJsExtensionThis, src: string): MarkedJsToken | void {
        const match = ghStrikeThroughRegex.exec(src);

        if (match) {
          const [, text] = match;

          const token = {
            type: ghStrikeExtensionName,
            raw: match[0],
            text,
            tokens: [],
          };
          this.lexer.inline(token.text, token.tokens);

          return token;
        }
      },
      renderer(this: MarkedJsExtensionThis, { tokens }: MarkedJsToken): string {
        return `<s>${this.parser.parseInline(tokens)}</s>`;
      },
    };

    marked.use({ extensions: [ghMentionExtension, ghEmojiExtension, ghStrikeThroughExtension] });
  }
}
